/**
 * ShareAndEarnWidget — Bouton flottant "Partager et gagner"
 * Présent sur toutes les pages (monté dans App.js)
 * S'affiche uniquement si l'utilisateur a un code de parrainage (connecté)
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Share2, X, Copy, Check, MessageCircle, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ShareAndEarnWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [referralUrl, setReferralUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Masquer dans admin et livreur
  const hidden = location.pathname.startsWith('/admin') || location.pathname.startsWith('/livreur');

  // Charger le lien dès que l'user est connecté (endpoint protégé → jeton requis)
  useEffect(() => {
    if (user?.id && !referralUrl && !hidden) {
      const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
      fetch(`${BACKEND}/api/parrainage/code/${user.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.json())
        .then(d => setReferralUrl(d.referral_url || ''))
        .catch(() => {});
    }
  // eslint-disable-next-line
  }, [user, hidden]);

  if (hidden || !user || !referralUrl) return null;

  function copyLink() {
    navigator.clipboard?.writeText(referralUrl);
    setCopied(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopied(false), 2000);
  }

  const shareText = encodeURIComponent(`🎉 Créez votre événement avec Show Événement et profitez de mes avantages ! ${referralUrl}`);

  return (
    <div style={{ position: 'fixed', bottom: 90, right: 24, zIndex: 8990, fontFamily: 'Manrope, sans-serif' }}>
      {/* Panneau ouvert */}
      {open && (
        <div style={{
          width: 260,
          borderRadius: 18,
          background: '#FEFAF5',
          border: '1px solid rgba(212,175,55,0.3)',
          boxShadow: '0 16px 48px rgba(26,26,46,0.16)',
          marginBottom: 10,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #5DBCBF, #0B4B4E)', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={{ color: '#FFFFFF', fontSize: 13, fontWeight: 700, margin: 0 }}>🎁 Partager et gagner</p>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)' }}>
              <X size={16} />
            </button>
          </div>
          {/* Description */}
          <div style={{ padding: '12px 16px 10px' }}>
            <p style={{ fontSize: 12, color: 'rgba(26,26,46,0.65)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Partagez votre lien et gagnez{' '}
              <strong style={{ color: '#5DBCBF' }}>jusqu'à 15%</strong>{' '}
              sur chaque commande réalisée.
            </p>
            {/* Boutons partage */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a
                href={`https://wa.me/?text=${shareText}`}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 11, background: '#25D366', color: '#FFFFFF', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
              <a
                href={`sms:?body=${shareText}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 11, background: 'rgba(93,188,191,0.12)', color: '#0B4B4E', textDecoration: 'none', fontSize: 13, fontWeight: 600, border: '1px solid rgba(93,188,191,0.25)' }}
              >
                <Phone size={15} /> SMS
              </a>
              <button
                data-testid="share-earn-copy-btn"
                onClick={copyLink}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 14px', borderRadius: 11, background: '#FFFFFF', border: '1px solid var(--border)', fontSize: 12, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{referralUrl.replace('https://', '')}</span>
                {copied ? <Check size={14} style={{ color: '#1F9D7A', flexShrink: 0 }} /> : <Copy size={14} style={{ flexShrink: 0 }} />}
              </button>
            </div>
            <p style={{ fontSize: 10, color: 'rgba(26,26,46,0.4)', textAlign: 'center', margin: '8px 0 0' }}>
              +5€ bonus sur votre 1er parrainage validé
            </p>
          </div>
        </div>
      )}

      {/* Bouton principal */}
      <button
        data-testid="share-earn-btn"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '10px 18px',
          borderRadius: 50,
          background: open ? '#0B4B4E' : 'linear-gradient(135deg, #5DBCBF, #0B4B4E)',
          color: '#FFFFFF', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 700,
          boxShadow: '0 6px 20px rgba(93,188,191,0.4)',
          transition: 'all 0.2s',
        }}
      >
        <Share2 size={15} />
        Partager et gagner
      </button>
    </div>
  );
}
